import { PublicIdSchemas } from '@idc-forms/id'
import { z } from 'zod'
import { InstantSchema } from '../primitives/time'
import { V2ClientAccountSchema } from './auth'
import { V2CursorPaginationSchema } from './http'

/**
 * La ADMINISTRACIÓN de las cuentas de la app móvil, del lado del back-office.
 *
 * ── POR QUÉ ESTE ARCHIVO Y NO `auth.ts` ──────────────────────────────────────
 *
 * `auth.ts` es el contrato de CREDENCIALES y sesión: lo que devuelven las escrituras
 * (`V2ClientAccountSchema`, `V2ClientGrantSchema`) para confirmar qué quedó guardado.
 * Esto de acá es otro consumidor —una pantalla de back-office que responde «¿quién tiene
 * acceso, desde cuándo, y por qué lo perdió?»— y vive aparte por lo mismo que
 * `admin-clients.ts` o `admin-users.ts`.
 *
 * Extiende `V2ClientAccountSchema` en vez de redeclarar `id`/`email`/`active`/`blockedAt`:
 * la escritura y la lectura de la misma entidad no pueden divergir en los campos que
 * comparten, y heredarlos hace que un cambio en uno rompa el otro en el typecheck.
 *
 * ── LO QUE LA PANTALLA NECESITA Y LA API NO TENÍA ────────────────────────────
 *
 * Hasta acá el módulo publicaba CINCO escrituras y CERO lecturas: se podía crear una
 * cuenta, concederle un inmueble, revocarlo, bloquearla y reactivarla — y no se podía
 * ver ninguna de esas cosas. La única superficie de lectura era el estado por
 * negociación (`admin-negotiations.ts`), que responde por una venta y no por una
 * persona.
 */

/** El cliente dueño de la cuenta, con lo que la tabla pinta en su columna. */
export const V2AdminClientAccountClientSchema = z.object({
  /**
   * `null` cuando el expediente no resuelve — hoy, porque fue dado de baja.
   *
   * ☠️ NULLABLE Y NO CADENA VACÍA, y la diferencia no es de estilo. `PublicIdSchemas.client`
   * es un regex `^cli_`: un `''` **no pasa el contrato**. Como la cuenta sigue viva aunque
   * su expediente se dé de baja, esa fila salía con `id: ''` y el validador del borde
   * rechazaba la RESPUESTA ENTERA — un solo cliente dado de baja dejaba la consola sin
   * tabla, con las tarjetas de arriba diciendo «2 cuentas» al lado del hueco.
   *
   * Una fila que no se puede identificar del todo tiene que seguir siendo UNA fila mala, no
   * la página. Y la cuenta hay que verla igual: es precisamente el caso en que alguien
   * necesita entrar a revocarle el acceso.
   */
  id: PublicIdSchemas.client.nullable(),
  /**
   * Ya resuelto por tipo de persona: `applicant.fullname` para individual y
   * `applicant.companyName` para jurídica. La regla vive en el servidor porque una
   * empresa no tiene apellido y componer un nombre no es asunto de una tabla.
   *
   * Cadena vacía cuando el expediente todavía no tiene nombre cargado — pasa con las
   * importaciones viejas. Se publica vacío en vez de `null` para que la columna no
   * tenga dos formas de decir lo mismo.
   */
  name: z.string(),
  typePerson: z.enum(['individual', 'juridical'])
})

/**
 * Una fila del listado.
 *
 * `grantedProperties` es un NÚMERO y no el arreglo de inmuebles: la tabla necesita la
 * señal («tiene 2»), no el detalle. Publicar el arreglo obliga a resolver inmuebles por
 * cada fila de la página y deja que la tabla decida qué mostrar cuando son cinco, que
 * es una decisión que no le toca. El detalle está a un click, en el endpoint de detalle.
 */
export const V2AdminClientAccountRowSchema = V2ClientAccountSchema.extend({
  client: V2AdminClientAccountClientSchema,
  /** Grants vigentes: activos, no revocados y no vencidos. */
  grantedProperties: z.number().int().nonnegative(),
  /**
   * Si la persona YA ENTRÓ alguna vez.
   *
   * Sale de `firstAccessAt` y NO de si tiene sesiones abiertas: `auth_sessions` tiene
   * TTL, así que «sin sesiones» significa lo mismo para quien nunca entró que para quien
   * entró hace tres meses. La pantalla necesita distinguirlos — al primero se le
   * reenvía la invitación, al segundo sería un ridículo.
   */
  signedIn: z.boolean(),
  firstAccessAt: InstantSchema.nullable(),
  /**
   * La última vez que la credencial se usó de verdad (OTP verificado o refresh rotado).
   *
   * Campo propio de la cuenta y no derivado de `auth_sessions`, por el TTL de arriba.
   * `null` en las cuentas anteriores a que el campo existiera aunque hayan entrado: no
   * hay de dónde reconstruirlo y afirmar una fecha inventada es peor que declarar la
   * ausencia.
   */
  lastAccessAt: InstantSchema.nullable(),
  invitedAt: InstantSchema.nullable(),
  createdAt: InstantSchema
})

export const V2AdminClientAccountsPageSchema = z.object({
  data: z.array(V2AdminClientAccountRowSchema),
  pagination: V2CursorPaginationSchema,
  /**
   * `true` cuando la búsqueda por NOMBRE tocó su tope interno y la respuesta puede estar
   * incompleta.
   *
   * Se publica en vez de esconderse porque el tope existe: el nombre no vive en
   * `client_accounts`, así que buscar por él resuelve primero los clientes que matchean
   * y ese paso está acotado para que una búsqueda de una letra no barra la cartera
   * entera. La pantalla tiene que poder decir «mostrando los primeros resultados,
   * afiná la búsqueda» — un listado silenciosamente truncado se lee como «no existe».
   *
   * Siempre `false` cuando no hay `search`.
   */
  searchTruncated: z.boolean()
})

/* ────────────────────────────────────────────────────────────────────────────
 * DETALLE
 * ──────────────────────────────────────────────────────────────────────────── */

/** El inmueble de un grant, con el proyecto que lo ubica. */
export const V2AdminClientGrantPropertySchema = z
  .object({
    id: PublicIdSchemas.property,
    code: z.string(),
    name: z.string(),
    project: z.object({ id: PublicIdSchemas.project, name: z.string() }).nullable()
  })
  .nullable()

/** La negociación que originó el acceso, con el estado que soporte necesita leer. */
export const V2AdminClientGrantNegotiationSchema = z
  .object({
    id: PublicIdSchemas.negotiation,
    code: z.number().int().nonnegative(),
    state: z
      .object({
        id: PublicIdSchemas.state,
        name: z.string(),
        color: z.string().nullable()
      })
      .nullable()
  })
  .nullable()

/** El cliente de la cuenta y su rol dentro de la negociación resuelta. */
export const V2AdminClientGrantClientSchema = V2AdminClientAccountClientSchema.extend({
  /** `null` cuando no existe una negociación desde la cual resolver la titularidad. */
  principal: z.boolean().nullable()
})

/**
 * Un acceso a un inmueble, VIVO O MUERTO.
 *
 * Los revocados viajan a propósito. «¿Por qué el cliente dejó de ver su inmueble?» es
 * *la* pregunta de soporte, y tres de los cinco motivos son automáticos (vencimiento de
 * la negociación, rescisión, inactividad): un listado que sólo muestra los vigentes
 * convierte una revocación del sistema en un misterio que termina en una llamada.
 *
 * Sin paginación: el conjunto lo acota cuántos inmuebles tiene una persona, que son
 * unidades.
 */
export const V2AdminClientGrantSchema = z.object({
  id: PublicIdSchemas.accessGrant,
  negotiation: V2AdminClientGrantNegotiationSchema,
  property: V2AdminClientGrantPropertySchema,
  client: V2AdminClientGrantClientSchema,
  /**
   * Si el cliente PUEDE VER ESTE INMUEBLE AHORA. No es la bandera `active` de la fila.
   *
   * Un grant vencido sigue con `active: true` en la base hasta que alguien lo revoque, pero
   * el teléfono ya responde `CLIENT_APP_ACCESS_EXPIRED`. Publicar la bandera cruda hacía que
   * la consola dijera que el cliente tiene acceso mientras la app se lo niega — y «¿por qué
   * el cliente dejó de ver su inmueble?» es LA pregunta que esta pantalla existe para
   * contestar, así que contestarla mal es peor que no tenerla.
   *
   * Se resuelve con la MISMA regla que el contador del listado —vigente, sin revocar y sin
   * vencer—, porque las dos cifras se miran juntas: si divergen, la tabla dice «1 inmueble»
   * y el panel muestra dos, uno de ellos en verde.
   */
  active: z.boolean(),
  /**
   * Vencimiento programado; `null` = vive hasta que alguien lo revoque (ADR 0016).
   *
   * Puede estar EN EL PASADO con `revokedAt: null`: es la ventana entre que el grant vence y
   * que algo lo revoque. Ahí `active` ya es `false` y quien pinte la fecha tiene que decir
   * «venció», no «vence».
   */
  expiresAt: InstantSchema.nullable(),
  revokedAt: InstantSchema.nullable(),
  /**
   * Por qué murió. El catálogo es el de `grant-revocation-reason.ts` y la UI lo traduce
   * por CÓDIGO, nunca por el texto: es contrato, no copy.
   *
   * `null` mientras el grant vive. Puede llegar un valor fuera del catálogo conocido en
   * filas viejas, y por eso es `string` y no un `enum` cerrado: un motivo desconocido
   * tiene que poder mostrarse como «revocado» en vez de tumbar la pantalla entera al
   * validar.
   */
  revokedReason: z.string().nullable(),
  createdAt: InstantSchema
})

/**
 * Un dispositivo con sesión VIVA.
 *
 * Es la lista corta a propósito: `auth_sessions` tiene TTL, así que esto es «qué
 * teléfonos están conectados ahora», no un historial. Para el historial está la
 * actividad.
 */
export const V2AdminClientDeviceSchema = z.object({
  id: PublicIdSchemas.session,
  /** Cómo se llama el teléfono, si la app lo mandó al iniciar sesión. */
  deviceName: z.string().nullable(),
  platform: z.string().nullable(),
  ip: z.string().nullable(),
  lastUsedAt: InstantSchema.nullable(),
  createdAt: InstantSchema,
  expiresAt: InstantSchema
})

/**
 * Un evento de la bitácora de seguridad que toca a esta cuenta o a sus accesos.
 *
 * `action` es el código estable (`client_account.created`, `client_grant.revoked_…`) y
 * la UI lo traduce; `actor` es quién lo hizo, `null` cuando lo hizo el sistema —los
 * crons de vencimiento e inactividad no tienen persona detrás, y decir «Sistema» es
 * información, no un hueco—.
 */
/** Quién hizo un evento de la bitácora. SSOT: la API y la UI leen estos mismos valores. */
export const CLIENT_ACCOUNT_EVENT_ACTOR_KIND = {
  client: 'client',
  staff: 'staff',
  system: 'system'
} as const
export type ClientAccountEventActorKind =
  (typeof CLIENT_ACCOUNT_EVENT_ACTOR_KIND)[keyof typeof CLIENT_ACCOUNT_EVENT_ACTOR_KIND]

/** Tamaño de página de la bitácora y su tope; la API y la documentación leen estos valores. */
export const CLIENT_ACCOUNT_EVENTS_PAGE_SIZE = 30
export const CLIENT_ACCOUNT_EVENTS_MAX_PAGE_SIZE = 100

export const V2AdminClientAccountEventSchema = z.object({
  action: z.string(),
  at: InstantSchema,
  actor: z.object({ id: PublicIdSchemas.user.nullable(), fullName: z.string() }).nullable(),
  /**
   * Quién fue, aunque no haya nombre: la cuenta del cliente (`client`), una persona del equipo
   * (`staff`) o el sistema (crons). Sin esto un evento del cliente se leía como «Sistema».
   */
  actorKind: z.enum([
    CLIENT_ACCOUNT_EVENT_ACTOR_KIND.client,
    CLIENT_ACCOUNT_EVENT_ACTOR_KIND.staff,
    CLIENT_ACCOUNT_EVENT_ACTOR_KIND.system
  ]),
  /**
   * Datos ya filtrados por una lista blanca por acción (versión legal, conteos, correo
   * anterior y nuevo…). Escalares nada más: nunca IP, ids de credencial ni `metadata` cruda.
   */
  details: z.record(z.string(), z.union([z.string(), z.number(), z.boolean()])).optional()
})

/** Una página de la bitácora de la cuenta, del más nuevo al más viejo. */
export const V2AdminClientAccountEventsPageSchema = z.object({
  data: z.array(V2AdminClientAccountEventSchema),
  pagination: V2CursorPaginationSchema
})

/**
 * Query de las páginas siguientes de la bitácora.
 *
 * `before` es el `pagination.nextCursor` de la página anterior y hay que tratarlo como OPACO:
 * hoy codifica el instante y el id del último evento (para no saltear los que comparten el
 * mismo milisegundo en el borde de la página). Por compatibilidad también se acepta un
 * instante pelado, que pide lo estrictamente anterior.
 */
export const V2AdminClientAccountEventsQuerySchema = z.object({
  before: z.string().min(1).max(200).optional(),
  limit: z.coerce.number().int().min(1).max(CLIENT_ACCOUNT_EVENTS_MAX_PAGE_SIZE).optional()
})

export const V2AdminClientAccountDetailSchema = V2AdminClientAccountRowSchema.extend({
  grants: z.array(V2AdminClientGrantSchema),
  devices: z.array(V2AdminClientDeviceSchema),
  /** La primera página de movimientos, del más nuevo al más viejo. Acotada en el servidor. */
  events: z.array(V2AdminClientAccountEventSchema),
  /** Cómo seguir: las páginas más viejas salen de `GET /:id/events?before=<nextCursor>`. */
  eventsPagination: V2CursorPaginationSchema
})

/**
 * Los totales de la pantalla, con los MISMOS filtros del listado y sin paginar.
 *
 * ── POR QUÉ NO SON FACETAS, Y POR QUÉ IMPORTA ────────────────────────────────
 *
 * El resto de los listados v2 publica facetas —conteos por los valores de UNA dimensión,
 * que suman el total y se pintan como chips excluyentes—. Acá eso sería MENTIR: una
 * cuenta bloqueada puede además no haber ingresado nunca, así que los tres números **se
 * solapan** y no particionan nada. Un chip que dice «Bloqueadas 3» al lado de «Nunca
 * ingresaron 12» invita a leer 15 cuentas donde puede haber 13.
 *
 * Por eso viajan como contadores independientes y la pantalla los pinta como TARJETAS,
 * no como chips: cada una aplica su propio filtro y ninguna promete ser una parte de un
 * todo.
 */
export const V2AdminClientAccountsSummarySchema = z.object({
  /** Cuántas cuentas hay bajo los filtros actuales. */
  total: z.number().int().nonnegative(),
  /** Ya entraron alguna vez (`firstAccessAt` presente). */
  signedIn: z.number().int().nonnegative(),
  /** La cola de trabajo: se les abrió acceso y nunca aparecieron. */
  neverSignedIn: z.number().int().nonnegative(),
  /** Bloqueadas. Puede solaparse con las dos de arriba. */
  blocked: z.number().int().nonnegative()
})

export type V2AdminClientAccountsSummary = z.infer<typeof V2AdminClientAccountsSummarySchema>

/* ────────────────────────────────────────────────────────────────────────────
 * ESCRITURAS QUE FALTABAN
 * ──────────────────────────────────────────────────────────────────────────── */

/**
 * Resultado del reenvío de la invitación.
 *
 * `202` y no `200`: el correo sale por evento —un fallo de envío no puede tumbar la
 * respuesta— así que lo único que la API puede afirmar es que lo aceptó.
 */
export const V2ResendClientInvitationSchema = z.object({
  id: PublicIdSchemas.clientAccount,
  /** Cuándo quedó registrado este envío. Es lo que arma la ventana del próximo. */
  invitedAt: InstantSchema
})

/**
 * Corregir el correo de una cuenta.
 *
 * ── POR QUÉ EXISTE ───────────────────────────────────────────────────────────
 *
 * Porque sin esto un correo mal tipeado deja la cuenta MUERTA y sin salida: el código de
 * acceso viaja a un buzón que no existe, la cuenta no se puede borrar, y como el correo
 * es único, abrir otra funciona pero deja huérfana a la primera con sus accesos. El
 * `reason: 'email-taken'` que el estado del expediente ya sabe reportar tampoco tenía
 * ningún camino de resolución en el producto.
 *
 * Sólo el correo. Cambiar de cliente sería otra cuenta, no una edición.
 */
export const V2UpdateClientAccountSchema = z.object({
  email: z.string().email()
})

export type V2ResendClientInvitation = z.infer<typeof V2ResendClientInvitationSchema>
export type V2UpdateClientAccountInput = z.infer<typeof V2UpdateClientAccountSchema>
export type V2AdminClientAccountRow = z.infer<typeof V2AdminClientAccountRowSchema>
export type V2AdminClientAccountsPage = z.infer<typeof V2AdminClientAccountsPageSchema>
export type V2AdminClientGrant = z.infer<typeof V2AdminClientGrantSchema>
export type V2AdminClientDevice = z.infer<typeof V2AdminClientDeviceSchema>
export type V2AdminClientAccountEvent = z.infer<typeof V2AdminClientAccountEventSchema>
export type V2AdminClientAccountEventsPage = z.infer<typeof V2AdminClientAccountEventsPageSchema>
export type V2AdminClientAccountEventsQuery = z.infer<typeof V2AdminClientAccountEventsQuerySchema>
export type V2AdminClientAccountDetail = z.infer<typeof V2AdminClientAccountDetailSchema>
